/**
 * Classic main screen register form (`main_screen_variant: classic`).
 * Tune this file only for classic — hero_dock has its own copy under `variants/hero_dock/`.
 * Terms accordion: Phase A settles keyboard/lift, Phase B runs shared progress (UI thread).
 */
import React, { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import { View, Text, Alert, ScrollView, Platform, Keyboard } from 'react-native';
import Animated, {
  Extrapolation,
  cancelAnimation,
  interpolate,
  useAnimatedStyle,
  useReducedMotion,
  useSharedValue,
  withTiming,
} from 'react-native-reanimated';
import { Clickable2 } from '../../../components/common/Clickable2';
import { ButtonLoader } from '../../../components/load-main/ButtonLoader';
import { Input } from '../../../components/common/Input';
import { AppPickerTrigger } from '../../../components/common/AppPickerTrigger';
import { AreaPickerModal } from '../../../components/common/AreaPickerModal';
import { RegisterTermsSection } from '../../RegisterTermsSection';
import { prefetchRegisterTermsHtml } from '../../registerTermsCms';
import {
  REGISTER_TERMS_MOTION,
  settleRegisterFormBeforeTerms,
  waitMs,
} from '../../registerTermsMotion';
import { appTheme, useThemeColors } from '../../../shared/theme';
import { postRegisterRequest, registerSuccessPersistToStore, enrichSignupOtpParams } from '../../../shared/auth';
import { getFcmDeviceTokenForAuth } from '../../../platform/notifications/fcmDeviceToken';
import { useAreas } from '../../../data/domains/addresses';
import { useOrganizationSettings, isHasLastNameEnabled, resolvePhoneMaxDigits } from '../../../data/domains/config';
import { useTranslation } from '../../../shared/i18n';
import { useRtl } from '../../../shared/i18n/useRtl';
import { setSelectedSubscription } from '../../../data/domains/subscription';
import {
  sharedStyles,
  formFieldHalf,
  formFieldRow,
  createSolidPrimaryCtaLabelStyle,
} from '../../../shared/theme/sharedStyles';
import {
  createCredentialFormSheetStyles,
  createCredentialPrimaryCtaSurfaceStyle,
  credentialFormPrimaryButtonWrap,
  createRegistrationFormSheetStyles,
} from '../../../shared/theme/styles/sheets';
import { useAuthSheets } from '../../../shared/auth/authSheetContext';
import { authSheetFormSpacing, AUTH_FORM_KEYBOARD, useAuthFormScrollMaxHeight } from '../../../shared/auth';
import { useAuthInputKeyboardAvoidance } from '../../keyboard/useAuthInputKeyboardAvoidance';
import { AuthFocusTarget } from '../../keyboard/AuthFocusTarget';

export type RegisterSheetContentProps = {
  onDismiss: () => void;
  subData?: unknown;
  /** Extra auth modal lift when scroll cannot clear the focused field (overlap only, never full keyboard height). */
  onKeyboardPanelNudge?: (offset_px: number) => void;
};

export function RegisterSheetContent({
  onDismiss: _onDismiss,
  subData,
  onKeyboardPanelNudge,
}: RegisterSheetContentProps) {
  const { t, locale } = useTranslation();
  const rtl = useRtl();
  const colors = useThemeColors() ?? appTheme.colors;
  const layout = useMemo(() => createCredentialFormSheetStyles(colors), [colors]);
  const styles = useMemo(() => createRegistrationFormSheetStyles(colors), [colors]);
  const scroll_max_h = useAuthFormScrollMaxHeight();
  const reduce_motion = useReducedMotion();
  const { openLogin, openOtp } = useAuthSheets();
  const {
    settings: { allowedDigitForPhone: userPhoneAllowedDigit, dialing_code: dialing_code, showReferralField: showRef },
  } = useOrganizationSettings();
  const has_last_name_enabled = isHasLastNameEnabled();
  const app_language = locale === 'ar' ? 'ar' : 'en';

  useEffect(() => {
    void prefetchRegisterTermsHtml(app_language);
  }, [app_language]);

  const display_dialing_code = dialing_code != null ? String(dialing_code) : '965';
  const phone_max_digits = resolvePhoneMaxDigits(userPhoneAllowedDigit, display_dialing_code);

  const [formData, setFormData] = useState({
    name: '',
    last_name: '',
    email: '',
    phone: '',
    password: '',
    areasOrRegionId: '',
    governorate_id: '',
    referral_code: '',
    dialing_code: null,
    device_type: Platform.OS,
    device_token: '',
  });

  const [tcChecked, setTcChecked] = useState(false);
  const [terms_open, setTermsOpen] = useState(false);
  const [terms_busy, setTermsBusy] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedAreaName, setSelectedAreaName] = useState('');
  const [showAreaPicker, setShowAreaPicker] = useState(false);
  const [errors, setErrors] = useState<Record<string, boolean>>({});

  const scroll_ref = useRef<ScrollView>(null);
  const scroll_y = useRef(0);
  const name_row_ref = useRef<View>(null);
  const email_block_ref = useRef<View>(null);
  const phone_block_ref = useRef<View>(null);
  const password_block_ref = useRef<View>(null);
  const area_block_ref = useRef<View>(null);
  const referral_block_ref = useRef<View>(null);
  const panel_lift_ref = useRef(0);
  const terms_busy_ref = useRef(false);
  const open_generation_ref = useRef(0);

  const terms_progress = useSharedValue(0);
  const fields_h_sv = useSharedValue(0);
  const actions_h_sv = useSharedValue(0);

  const handleTermsAccept = useCallback(() => {
    setTcChecked(true);
    setErrors(prev => ({ ...prev, tc: false }));
  }, []);

  const on_terms_checkbox_change = useCallback((next: boolean) => {
    setTcChecked(next);
    setErrors(prev => ({ ...prev, tc: false }));
  }, []);

  const on_panel_nudge = useCallback(
    (offset_px: number) => {
      panel_lift_ref.current = offset_px > 0 ? offset_px : 0;
      onKeyboardPanelNudge?.(offset_px);
    },
    [onKeyboardPanelNudge],
  );

  const {
    on_field_focus,
    on_scroll_layout,
    on_scroll_content_size_change,
    keyboard_scroll_pad,
    clear_panel_offset,
  } = useAuthInputKeyboardAvoidance({
    enabled: !terms_open && !terms_busy,
    scroll_ref,
    scroll_y_ref: scroll_y,
    on_panel_offset: on_panel_nudge,
    clearance_px: AUTH_FORM_KEYBOARD.fieldFocusClearancePx,
  });

  const run_terms_progress = useCallback(
    (to_open: boolean) => {
      const duration = reduce_motion ? 1 : REGISTER_TERMS_MOTION.durationMs;
      cancelAnimation(terms_progress);
      terms_progress.value = withTiming(to_open ? 1 : 0, {
        duration,
        easing: REGISTER_TERMS_MOTION.easing,
      });
    },
    [reduce_motion, terms_progress],
  );

  const request_terms_open = useCallback(async () => {
    if (terms_busy_ref.current || terms_open) {
      return;
    }
    terms_busy_ref.current = true;
    setTermsBusy(true);
    const generation = ++open_generation_ref.current;

    try {
      await settleRegisterFormBeforeTerms({
        panel_lift_px: panel_lift_ref.current,
        keyboard_scroll_pad,
        clear_panel_offset,
      });

      if (generation !== open_generation_ref.current) {
        return;
      }

      scroll_ref.current?.scrollTo({ y: 0, animated: false });

      setTermsOpen(true);
      await waitMs(reduce_motion ? 0 : 32);

      if (generation !== open_generation_ref.current) {
        return;
      }

      run_terms_progress(true);
    } finally {
      if (generation === open_generation_ref.current) {
        terms_busy_ref.current = false;
        setTermsBusy(false);
      }
    }
  }, [clear_panel_offset, keyboard_scroll_pad, reduce_motion, run_terms_progress, terms_open]);

  const request_terms_close = useCallback(() => {
    if (terms_busy_ref.current && !terms_open) {
      open_generation_ref.current += 1;
      terms_busy_ref.current = false;
      setTermsBusy(false);
      return;
    }
    if (!terms_open && terms_progress.value < 0.01) {
      return;
    }
    setTermsOpen(false);
    run_terms_progress(false);
  }, [run_terms_progress, terms_open, terms_progress]);

  const { data: areas, isLoading: areasLoading, error: areasError, refetch: refetchAreas } = useAreas();

  const updateField = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setErrors(prev => ({ ...prev, [field]: false }));
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, boolean> = {};
    if (!formData.name || formData.name.trim().length < 2) {
      newErrors.name = true;
    }
    if (has_last_name_enabled && (!formData.last_name || formData.last_name.trim().length < 2)) {
      newErrors.last_name = true;
    }
    if (!formData.email || formData.email.trim().length < 5) {
      newErrors.mail = true;
    }
    if (!formData.phone || formData.phone.trim().length < 6) {
      newErrors.phone = true;
    }
    if (!formData.password || formData.password.trim().length < 6) {
      newErrors.password = true;
    }
    if (!formData.areasOrRegionId) {
      newErrors.area = true;
    }
    if (!tcChecked) {
      newErrors.tc = true;
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleRegister = async () => {
    if (!validateForm()) {
      Alert.alert(t('common.error'), t('auth.fillAllFields'));
      return;
    }

    setIsLoading(true);

    try {
      const device_token = await getFcmDeviceTokenForAuth();
      const payload = {
        ...formData,
        last_name: has_last_name_enabled ? formData.last_name : formData.name,
        device_token,
        TcChecked: tcChecked,
      };
      const response = (await postRegisterRequest(payload)) as {
        success?: boolean;
        message?: string;
        data?: { customer?: unknown };
        customer?: unknown;
      };
      if (response.success !== true) {
        Alert.alert(t('common.error'), response.message || t('auth.registrationFailed'));
        return;
      }
      const has_customer = Boolean(
        (response.data && (response.data as { customer?: unknown }).customer) ?? (response as { customer?: unknown }).customer
      );
      if (has_customer) {
        const persist = await registerSuccessPersistToStore(response);
        if (!persist.ok) {
          Alert.alert(t('common.error'), persist.message || t('auth.registrationFailed'));
          return;
        }
      }

      /** Guest plan flow: address first, then checkout with plan from `selectedSubscriptionStorage`. */
      const redirectRoute = subData ? 'AddressForm' : 'SubscriptionList';
      const navigationPayload: Record<string, unknown> = {
        phone: formData.phone,
        countryCode: dialing_code != null ? `+${dialing_code}` : '+965',
        context: 'signup',
        redirectTo: redirectRoute,
        signupPassword: formData.password,
      };

      if (subData) {
        navigationPayload.checkoutPayload = subData;
        await setSelectedSubscription(subData as object);
      }

      openOtp(enrichSignupOtpParams(navigationPayload));
    } catch (error: any) {
      if (error.status === 422) {
        Alert.alert(t('common.error'), t('auth.fillAllFieldsCorrectly'));
      } else {
        Alert.alert(t('common.error'), error.message || t('auth.unexpectedError'));
      }
    } finally {
      setIsLoading(false);
    }
  };

  const selectArea = (area: any) => {
    Keyboard.dismiss();
    updateField('areasOrRegionId', String(area.id));
    updateField('governorate_id', area.governorate_id != null && area.governorate_id !== '' ? String(area.governorate_id) : '');
    setSelectedAreaName(area.title ?? area.name ?? '');
    setErrors(prev => ({ ...prev, area: false }));
    setShowAreaPicker(false);
  };

  const auth_primary_cta = useMemo(
    () => ({
      surface: createCredentialPrimaryCtaSurfaceStyle(colors),
      label: createSolidPrimaryCtaLabelStyle(colors),
    }),
    [colors],
  );

  const fields_collapse_style = useAnimatedStyle(() => {
    const measured_h = fields_h_sv.value;
    const open_max = measured_h > 0 ? measured_h : 10000;
    return {
      maxHeight: interpolate(terms_progress.value, [0, 1], [open_max, 0], Extrapolation.CLAMP),
      opacity: interpolate(terms_progress.value, [0, 0.35], [1, 0], Extrapolation.CLAMP),
      overflow: 'hidden' as const,
    };
  });

  const actions_collapse_style = useAnimatedStyle(() => {
    const measured_h = actions_h_sv.value;
    const open_max = measured_h > 0 ? measured_h : 10000;
    return {
      maxHeight: interpolate(terms_progress.value, [0, 1], [open_max, 0], Extrapolation.CLAMP),
      opacity: interpolate(terms_progress.value, [0, 0.3], [1, 0], Extrapolation.CLAMP),
      overflow: 'hidden' as const,
    };
  });

  return (
    <>
      <View style={layout.keyboardView}>
        <ScrollView
          ref={scroll_ref}
          onScroll={e => {
            scroll_y.current = e.nativeEvent.contentOffset.y;
          }}
          onLayout={on_scroll_layout}
          onContentSizeChange={on_scroll_content_size_change}
          scrollEventThrottle={16}
          style={[layout.scroll, { maxHeight: scroll_max_h }]}
          contentContainerStyle={[
            layout.scrollContent,
            {
              paddingTop: authSheetFormSpacing.formPaddingTop,
              paddingBottom: authSheetFormSpacing.formPaddingBottom + keyboard_scroll_pad,
            },
          ]}
          showsVerticalScrollIndicator={false}
          keyboardShouldPersistTaps="handled"
          keyboardDismissMode="on-drag"
          automaticallyAdjustKeyboardInsets={false}
          bounces={false}>
          <Animated.View style={fields_collapse_style} pointerEvents={terms_open ? 'none' : 'auto'}>
            <View
              style={layout.fields}
              collapsable={false}
              onLayout={e => {
                const h = Math.ceil(e.nativeEvent.layout.height);
                if (h > 0 && terms_progress.value < 0.05) {
                  fields_h_sv.value = h;
                }
              }}>
              <AuthFocusTarget ref={name_row_ref} style={has_last_name_enabled ? formFieldRow : undefined}>
                <Input
                  value={formData.name}
                  onChangeText={v => updateField('name', v)}
                  onFocus={() => on_field_focus(name_row_ref)}
                  placeholder={t('auth.enterFirstName')}
                  error={errors.name ? t('auth.fillAllFields') : undefined}
                  containerStyle={[sharedStyles.inputContainerFlush, has_last_name_enabled ? formFieldHalf : undefined]}
                />
                {has_last_name_enabled ? (
                  <Input
                    value={formData.last_name}
                    onChangeText={v => updateField('last_name', v)}
                    onFocus={() => on_field_focus(name_row_ref)}
                    placeholder={t('auth.enterLastName') || 'Enter last name'}
                    error={errors.last_name ? t('auth.fillAllFields') : undefined}
                    containerStyle={[sharedStyles.inputContainerFlush, formFieldHalf]}
                  />
                ) : null}
              </AuthFocusTarget>
              <AuthFocusTarget ref={email_block_ref}>
                <Input
                  value={formData.email}
                  onChangeText={v => updateField('email', v)}
                  onFocus={() => on_field_focus(email_block_ref)}
                  placeholder={t('auth.enterEmail')}
                  keyboardType="email-address"
                  autoCapitalize="none"
                  error={errors.email ? t('auth.fillAllFields') : undefined}
                  containerStyle={sharedStyles.inputContainerFlush}
                />
              </AuthFocusTarget>
              <AuthFocusTarget ref={phone_block_ref}>
                <Input
                  label={t('auth.phoneNumber')}
                  value={formData.phone}
                  onChangeText={v => updateField('phone', v.replace(/\D/g, '').slice(0, phone_max_digits))}
                  onFocus={() => on_field_focus(phone_block_ref)}
                  placeholder={t('auth.phonePlaceholder')}
                  keyboardType="phone-pad"
                  maxLength={phone_max_digits}
                  error={errors.phone ? t('auth.fillAllFields') : undefined}
                  containerStyle={sharedStyles.inputContainerFlush}
                  leftAccessory={<Text style={styles.countryCodeText}>+{display_dialing_code}</Text>}
                />
              </AuthFocusTarget>
              <AuthFocusTarget ref={password_block_ref}>
                <Input
                  value={formData.password}
                  onChangeText={v => updateField('password', v)}
                  onFocus={() => on_field_focus(password_block_ref)}
                  placeholder={t('auth.enterPassword') || 'Enter password'}
                  secureTextEntry
                  error={errors.password ? t('auth.fillAllFields') : undefined}
                  containerStyle={sharedStyles.inputContainerFlush}
                />
              </AuthFocusTarget>
              <AuthFocusTarget ref={area_block_ref}>
                <AppPickerTrigger
                  value={selectedAreaName || null}
                  placeholder={areasLoading ? (t('common.loading') ?? 'Loading...') : areasError && (areas?.length ?? 0) === 0 ? (t('common.retry') ?? 'Tap to retry') : t('auth.selectArea')}
                  onPress={() => (areasError && (areas?.length ?? 0) === 0 ? refetchAreas() : setShowAreaPicker(true))}
                  disabled={areasLoading || terms_busy}
                  loading={areasLoading}
                  error={errors.area ? t('auth.required') : undefined}
                  accessibilityLabel={t('auth.selectArea')}
                  containerStyle={sharedStyles.inputContainerFlush}
                />
              </AuthFocusTarget>
              {showRef ? (
                <AuthFocusTarget ref={referral_block_ref}>
                  <Input
                    value={formData.referral_code}
                    onChangeText={v => updateField('referral_code', v)}
                    onFocus={() => on_field_focus(referral_block_ref)}
                    placeholder={t('auth.enterReferralCode')}
                    autoCapitalize="characters"
                    containerStyle={sharedStyles.inputContainerFlush}
                  />
                </AuthFocusTarget>
              ) : null}
            </View>
          </Animated.View>

          <RegisterTermsSection
            checked={tcChecked}
            error={!!errors.tc}
            progress={terms_progress}
            open={terms_open}
            interaction_locked={terms_busy}
            onRequestOpen={request_terms_open}
            onRequestClose={request_terms_close}
            onCheckedChange={on_terms_checkbox_change}
            onAccept={handleTermsAccept}
          />

          <Animated.View style={actions_collapse_style} pointerEvents={terms_open ? 'none' : 'auto'}>
            <View
              collapsable={false}
              onLayout={e => {
                const h = Math.ceil(e.nativeEvent.layout.height);
                if (h > 0 && terms_progress.value < 0.05) {
                  actions_h_sv.value = h;
                }
              }}>
              <View style={sharedStyles.buttonStack}>
                <Clickable2
                  testID="create-account-continue"
                  onPress={handleRegister}
                  disabled={isLoading || terms_busy}
                  haptics
                  hapticType="impactLight"
                  style={{ alignSelf: 'stretch', flexShrink: 0 }}
                  accessibilityLabel={t('auth.createAccount')}
                  accessibilityRole="button"
                  accessibilityState={{ disabled: isLoading || terms_busy }}
                >
                  <View style={credentialFormPrimaryButtonWrap(colors)}>
                    <View style={auth_primary_cta.surface}>
                      {isLoading ? (
                        <ButtonLoader size={16} color={colors.onPrimary ?? colors.primaryText ?? colors.white} />
                      ) : (
                        <Text style={auth_primary_cta.label}>{t('auth.createAccount')}</Text>
                      )}
                    </View>
                  </View>
                </Clickable2>
              </View>

              <View style={{ marginTop: authSheetFormSpacing.afterPrimaryActions }}>
                <View style={[styles.loginRow, rtl.flipRowStyle]}>
                  <Text style={[styles.loginText, rtl.textFollowLocale]}>{t('auth.alreadyHaveAccount')}</Text>
                  <Clickable2
                    testID="create-account-signin"
                    onPress={() => openLogin({ presentation: 'default' })}
                    accessibilityRole="link"
                    accessibilityLabel={t('auth.signIn')}
                    hitSlop={appTheme.hitSlop.small}
                  >
                    <Text style={[styles.loginLink, rtl.textFollowLocale]}>{t('auth.signIn')}</Text>
                  </Clickable2>
                </View>
              </View>
            </View>
          </Animated.View>
        </ScrollView>
      </View>

      <AreaPickerModal visible={showAreaPicker} areas={areas ?? []} onSelect={selectArea} onClose={() => setShowAreaPicker(false)} selectedAreaId={formData.areasOrRegionId} />
    </>
  );
}

export default RegisterSheetContent;
